import React from "react";
import { Link } from "react-router-dom";
import { ServiceCatalogSectionData } from "../../types/interfaces";
import "../../styles/ServiceCatalogSection.css";

interface CatalogLinkProps {
  href: string;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "request";
}

const CatalogLink: React.FC<CatalogLinkProps> = ({
  href,
  children,
  variant = "primary",
}) => {
  const className = `service-catalog-link ${variant}`;
  const isExternalLink = /^https?:\/\//i.test(href);
  const isAnchorLink = href.startsWith("#");

  if (isExternalLink || isAnchorLink) {
    return (
      <a
        className={className}
        href={href}
        target={isExternalLink ? "_blank" : undefined}
        rel={isExternalLink ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
};

const ServiceCatalogSection: React.FC<ServiceCatalogSectionData> = ({
  id,
  title,
  subtitle,
  groups,
}) => {
  const groupId = (title: string) =>
    `catalog-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

  return (
    <section className="service-catalog-section" id={id}>
      <div className="service-catalog-inner">
        <div className="service-catalog-header">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <nav className="service-catalog-jump" aria-label="Browse service categories">
          {groups.map((group) => (
            <a key={group.title} href={`#${groupId(group.title)}`}>{group.title}</a>
          ))}
        </nav>

        <div className="service-catalog-groups">
          {groups.map((group) => (
            <div className="service-catalog-group" id={groupId(group.title)} key={group.title}>
              <div className="service-catalog-group-heading">
                <h3>{group.title}</h3>
                <p>{group.description}</p>
              </div>

              <div className="service-catalog-grid">
                {group.items.map((item) => (
                  <article className="service-catalog-card" key={item.title}>
                    <div className="service-catalog-card-topline">
                      <span className="service-catalog-status">
                        {item.status}
                      </span>
                    </div>
                    <h4>{item.title}</h4>
                    <p className="service-catalog-description">
                      {item.description}
                    </p>

                    <dl className="service-catalog-meta">
                      <div>
                        <dt>Audience</dt>
                        <dd>{item.audience}</dd>
                      </div>
                      <div>
                        <dt>Outcome</dt>
                        <dd>{item.outcome}</dd>
                      </div>
                      {item.poweredBy && (
                        <div>
                          <dt>Platform</dt>
                          <dd>{item.poweredBy}</dd>
                        </div>
                      )}
                    </dl>

                    {item.tags && item.tags.length > 0 && (
                      <div className="service-catalog-tags">
                        {item.tags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className="service-catalog-actions">
                      <CatalogLink href={item.ctaLink}>
                        {item.ctaText}
                      </CatalogLink>
                      {item.secondaryCtaLink && item.secondaryCtaText && (
                        <CatalogLink
                          href={item.secondaryCtaLink}
                          variant="secondary"
                        >
                          {item.secondaryCtaText}
                        </CatalogLink>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="service-catalog-help">
          <div>
            <strong>Need a tailored solution?</strong>
            <p>Tell us what you are building and we will help choose a practical starting point.</p>
          </div>
          <Link to="/contact">Talk to AlluraTech <span aria-hidden="true">→</span></Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceCatalogSection;
